import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DirectOrderProvider } from './context/DirectOrderContext';
import { NotificationProvider } from './context/NotificationContext';
import { ClientAdminProvider } from './context/ClientAdminContext';
import { ActiveFormsProvider, useActiveForms } from './context/ActiveFormsContext';
import AppLayout from './components/AppLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import LandingPage from './pages/LandingPage';
import DirectOrderDashboard from './pages/Employee/DirectOrderDashboard';
import DirectOrder from './pages/Employee/DirectOrder';
import CreateDirectOrder from './pages/Employee/CreateDirectOrder';
import ViewDirectOrder from './pages/Employee/ViewDirectOrder';
import ClientAdmin from './pages/Employee/ClientAdmin';
import ClientAdminHome from './pages/Employee/ClientAdminHome';
import ArchivedFormResponses from './pages/Employee/ArchivedFormResponses';
import SupportTickets from './pages/Employee/SupportTickets';
import Overtime from './pages/Employee/Overtime';
import MyAccount from './pages/Employee/MyAccount';
import EmployeeAdmin from './pages/Employee/EmployeeAdmin';
import CCOrdersPage from './pages/Employee/CCOrdersPage';
import Finance from './pages/Employee/Finance';
import ClientPortal from './pages/Clients/ClientPortal';
import RockNMalta from './pages/Clients/RockNMalta';
import GhannejjaGodda from './pages/Clients/GhannejjaGodda';
import Dfm2026 from './pages/Clients/Dfm2026';
import DanceOn from './pages/Clients/DanceOn';
import DynamicForm from './pages/Clients/DynamicForm';
import FormExpired from './pages/Clients/FormExpired';
import FormPreview from './pages/Clients/FormPreview';
import InventoryManagement from './pages/Employee/InventoryManagement';
import InventoryPrivacyPolicy from './pages/InventoryPrivacyPolicy';
import CallForQuote from './pages/Employee/CallForQuote';
import CfqRequest from './pages/Employee/CfqRequest';
import CfqDetail from './pages/Employee/CfqDetail';
import ResponseRecorded from './pages/ResponseRecorded';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

// Admin Route Component (validated server-side)
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Server-side validation - roles are fetched from backend
  if (!user.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Employee Admin Route Component (validated server-side)
const EmployeeAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Server-side validation - roles are fetched from backend
  if (!user.isEmployeeAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Direct Order Admin Route Component (validated server-side)
const DirectOrderAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Server-side validation - roles are fetched from backend
  if (!user.isDirectOrderAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Finance Admin Route Component (validated server-side)
const FinanceAdminRoute = ({ children }) => {
  const { user, loading, hasLineVotesAccess } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Server-side validation - roles are fetched from backend
  if (!hasLineVotesAccess) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return !user ? children : <Navigate to="/dashboard" replace />;
};

// Form Protected Route Component (check if form is active)
const FormProtectedRoute = ({ children, formName }) => {
  const { isFormActive, getFirstActiveForm, loading } = useActiveForms();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  // If this form is active, allow access
  if (isFormActive(formName)) {
    return children;
  }
  
  // If this form is not active, redirect to first active form or client portal
  const firstActiveForm = getFirstActiveForm();
  
  if (firstActiveForm === 'RockNMalta') {
    return <Navigate to="/client-portal/rocknmalta" replace />;
  } else if (firstActiveForm === 'Għannejja Ġodda') {
    return <Navigate to="/client-portal/ghannejja" replace />;
  }
  
  // No active forms, redirect to client portal home
  return <Navigate to="/client-portal" replace />;
};

// Inventory Manager Route Component
const InventoryManagerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isInventoryManager) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
          zIndexPopupBase: 1100,
        },
      }}
    >
      <AntApp>
        <AuthProvider>
          <NotificationProvider>
            <DirectOrderProvider>
              <ClientAdminProvider>
                <ActiveFormsProvider>
                <Router>
                <AppLayout>
                <div className="App">
                  <Routes>
                  {/* Landing Page - Public Route */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/inventory-privacy" element={<InventoryPrivacyPolicy />} />
                  <Route path="/response-recorded" element={<ResponseRecorded />} />
                  
                  {/* Public Routes */}
                  <Route 
                    path="/login" 
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    } 
                  />
                  <Route 
                    path="/register" 
                    element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    } 
                  />
                  <Route 
                    path="/forgot-password" 
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    } 
                  />
                  
                  {/* Protected Routes */}
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/direct-order" 
                    element={
                      <ProtectedRoute>
                        <DirectOrder />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/direct-order/create" 
                    element={
                      <ProtectedRoute>
                        <CreateDirectOrder />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/direct-order/view" 
                    element={
                      <ProtectedRoute>
                        <ViewDirectOrder />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/support/tickets"
                    element={
                      <ProtectedRoute>
                        <SupportTickets />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/client-admin"
                    element={
                      <ProtectedRoute>
                        <ClientAdminHome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/client-admin/archive/:formKey"
                    element={
                      <ProtectedRoute>
                        <ArchivedFormResponses />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/client-applications" 
                    element={
                      <ProtectedRoute>
                        <ClientAdmin />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/overtime" 
                    element={
                      <ProtectedRoute>
                        <Overtime />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/my-account" 
                    element={
                      <ProtectedRoute>
                        <MyAccount />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Client Portal Routes - Public Access (direct access disabled) */}
                  <Route path="/client-portal" element={<Navigate to="/" replace />} />
                  <Route path="/client-portal/rocknmalta" element={<RockNMalta />} />
                  <Route path="/client-portal/ghannejja" element={<GhannejjaGodda />} />
                  <Route path="/client-portal/danceon" element={<DanceOn />} />
                  <Route path="/client-portal/dfm2026communitycall" element={<Dfm2026 />} />
                  <Route path="/client-portal/:slug" element={<DynamicForm />} />
                  <Route path="/form-expired" element={<FormExpired />} />
                  {/* The public form page fed by the form editor's live preview (no API data) */}
                  <Route path="/form-preview" element={<FormPreview />} />
                  
                  {/* Direct Order Admin Only Routes */}
                  <Route 
                    path="/direct-order/dashboard" 
                    element={
                      <DirectOrderAdminRoute>
                        <DirectOrderDashboard />
                      </DirectOrderAdminRoute>
                    } 
                  />
                  <Route 
                    path="/direct-order/cc-orders" 
                    element={
                      <ProtectedRoute>
                        <CCOrdersPage />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Inventory - All Users */}
                  <Route 
                    path="/inventory" 
                    element={
                      <ProtectedRoute>
                        <InventoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/cfq" element={<ProtectedRoute><CallForQuote /></ProtectedRoute>} />
                  <Route path="/cfq/new" element={<ProtectedRoute><CfqRequest /></ProtectedRoute>} />
                  <Route path="/cfq/:id" element={<ProtectedRoute><CfqDetail /></ProtectedRoute>} />

                  {/* Employee Admin Only Routes */}
                  <Route 
                    path="/employee-admin" 
                    element={
                      <EmployeeAdminRoute>
                        <EmployeeAdmin />
                      </EmployeeAdminRoute>
                    } 
                  />
                  
                  {/* Finance Admin Only Routes */}
                  <Route 
                    path="/finance" 
                    element={
                      <FinanceAdminRoute>
                        <Finance />
                      </FinanceAdminRoute>
                    } 
                  />
                  
                  {/* Catch all route - redirect to landing page */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </AppLayout>
            </Router>
            </ActiveFormsProvider>
            </ClientAdminProvider>
          </DirectOrderProvider>
        </NotificationProvider>
      </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
